import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTable } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { DataSource } from '@angular/cdk/collections';
import { merge, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { CorrespondenceService } from '../../../domain/services/correspondence/correspondence.service';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { CorrespondenceEmail, CorrespondenceEmailStatus } from '../../../domain/models/correspondence.model';

class EmailListDataSource extends DataSource<CorrespondenceEmail> {
  paginator: MatPaginator | undefined;
  sort: MatSort | undefined;

  constructor(public data: CorrespondenceEmail[]) {
    super();
  }

  connect(): Observable<CorrespondenceEmail[]> {
    if (this.paginator && this.sort) {
      return merge(of(this.data), this.paginator.page, this.sort.sortChange).pipe(
        map(() => this.getPagedData(this.getSortedData([...this.data])))
      );
    }
    return of(this.data);
  }

  disconnect(): void {}

  private getPagedData(data: CorrespondenceEmail[]): CorrespondenceEmail[] {
    if (this.paginator) {
      const start = this.paginator.pageIndex * this.paginator.pageSize;
      return data.slice(start, start + this.paginator.pageSize);
    }
    return data;
  }

  private getSortedData(data: CorrespondenceEmail[]): CorrespondenceEmail[] {
    if (!this.sort?.active || this.sort.direction === '') return data;
    return data.sort((a, b) => {
      const isAsc = this.sort!.direction === 'asc';
      switch (this.sort!.active) {
        case 'sentDate': return compare(new Date(a.sentAt ?? a.scheduledAt ?? a.createdAt).getTime(), new Date(b.sentAt ?? b.scheduledAt ?? b.createdAt).getTime(), isAsc);
        case 'templateName': return compare(a.subject, b.subject, isAsc);
        case 'recipients': return compare(a.recipientCount, b.recipientCount, isAsc);
        case 'status': return compare(a.status, b.status, isAsc);
        default: return 0;
      }
    });
  }
}

function compare(a: string | number, b: string | number, isAsc: boolean): number {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

@Component({
  selector: 'app-email-list',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    RouterModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatChipsModule,
  ],
  templateUrl: './email-list.component.html',
  styleUrl: './email-list.component.css'
})
export class EmailListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<CorrespondenceEmail>;

  dataSource = new EmailListDataSource([]);
  allEmails: CorrespondenceEmail[] = [];

  displayedColumns = ['sentDate', 'templateName', 'audience', 'recipients', 'status'];

  filterStatus = '';
  filterStart!: Date;
  filterEnd!: Date;

  readonly statuses: CorrespondenceEmailStatus[] = ['SENT', 'SCHEDULED', 'QUEUED', 'SENDING', 'FAILED'];

  constructor(
    private readonly correspondenceService: CorrespondenceService,
    private readonly breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'mail', url: '/app/correspondence/emails', label: 'Correspondence' },
      { icon: '', url: '/app/correspondence/emails', label: 'Emails' }
    ]);
  }

  ngAfterViewInit(): void {
    this.correspondenceService.getEmails().subscribe(emails => {
      this.allEmails = emails;
      this.rebuildDataSource(this.applyFilters(emails));
    });
  }

  rebuildDataSource(data: CorrespondenceEmail[]): void {
    this.dataSource = new EmailListDataSource(data);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    if (this.table) {
      this.table.dataSource = this.dataSource;
    }
  }

  applyFilters(data: CorrespondenceEmail[]): CorrespondenceEmail[] {
    let filtered = [...data];
    if (this.filterStatus) {
      filtered = filtered.filter(e => e.status === this.filterStatus);
    }
    if (this.filterStart && this.filterEnd) {
      filtered = filtered.filter(e => {
        const t = new Date(e.sentAt ?? e.scheduledAt ?? e.createdAt).getTime();
        return t >= this.filterStart.getTime() && t <= this.filterEnd.getTime();
      });
    }
    return filtered;
  }

  onFilter(): void {
    this.rebuildDataSource(this.applyFilters(this.allEmails));
    if (this.paginator) this.paginator.pageIndex = 0;
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterStart = null!;
    this.filterEnd = null!;
    this.rebuildDataSource(this.allEmails);
  }

  getStatusClass(status: CorrespondenceEmailStatus): string {
    const map: Record<CorrespondenceEmailStatus, string> = {
      SENT: 'status-sent',
      SCHEDULED: 'status-scheduled',
      QUEUED: 'status-scheduled',
      SENDING: 'status-scheduled',
      FAILED: 'status-failed'
    };
    return map[status] ?? '';
  }
}
